import type { StorePublicProduct } from '@evi/shared';
import { familyForProduct, productPath, resolveProduct, SHOP_CATEGORIES, shopIcpBySlug } from '@evi/store-kit';
import { plainDescription, productDescription, productJsonLd } from './productFacts';

/**
 * Every shop URL's answer to a crawler: its status, title, description,
 * canonical URL and structured data. One pure function, read by the server
 * render (first response) and by the client (route changes), so the two agree.
 *
 * The origin is the stage's public site (VITE_SHOP_ORIGIN, sst.config.ts). Only
 * production is indexable; the staging site answers noindex on every page.
 */
const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};

export const SHOP_ORIGIN = (env['VITE_SHOP_ORIGIN'] || 'https://evinitiative.shop').replace(/\/$/, '');
export const SHOP_INDEXABLE = env['VITE_SHOP_INDEXABLE'] !== '0';

const STORE = 'EV Initiative Store';
const DESCRIPTION_MAX = 160;

/** Paths that are real pages, with their words. Category copy follows the
 *  landing page's category tiles. */
const STATIC_PAGES: Record<string, { title: string; description: string; image: string; needsCatalog?: boolean }> = {
  '/': {
    title: `EV Chargers for Business and Home | ${STORE}`,
    description: 'Buy Autel commercial DC fast chargers, Level 2 AC chargers and home EV chargers. DC prices include pre-commissioning to the EV Initiative network.',
    image: '/media/ugc/landing-poster.jpg',
  },
  '/commercial-dc': {
    title: `Commercial DC Fast Chargers, 40 kW to 240 kW | ${STORE}`,
    description: 'Autel DC Compact and MaxiCharger DC fast chargers, 40 kW to 240 kW. One inclusive price with pre-commissioning to the network. Buy online and ship to site.',
    image: '/media/products/dcfc/hero.webp',
    needsCatalog: true,
  },
  '/commercial-ac': {
    title: `Commercial Level 2 EV Chargers, up to 19.2 kW | ${STORE}`,
    description: 'Autel Level 2 EV chargers for workplaces, fleets and multifamily buildings, up to 19.2 kW. AC Elite Commercial, AC Pro and AC Ultra.',
    image: '/media/products/pedestal/hero.webp',
    needsCatalog: true,
  },
  '/home-charging': {
    title: `Home EV Chargers, Level 2 | ${STORE}`,
    description: 'Level 2 home EV chargers from Autel, 9.6 kW to 12 kW. The AC Elite Commercial joins the EV Initiative network over your home Wi-Fi.',
    image: '/media/products/wallbox/hero-elite-home.webp',
    needsCatalog: true,
  },
  '/accessories': {
    title: `EV Charger Accessories | ${STORE}`,
    description: 'Pedestal posts, RFID cards and mounting hardware for EV Initiative chargers.',
    image: '/media/products/accessories/pedestal-post-ac.jpg',
    needsCatalog: true,
  },
  '/incentives': {
    title: `EV Charger Incentives and Rebates | ${STORE}`,
    description: 'Check the EV charger incentives, rebates and tax credits available in your area, then buy the hardware that qualifies.',
    image: '/media/ugc/landing-poster.jpg',
  },
  '/sales-partners': {
    title: `Sales Partner Program for Installers | ${STORE}`,
    description: 'Sell and install EV chargers as an EV Initiative Sales Partner. See how the program works and apply.',
    image: '/media/ugc/landing-poster.jpg',
  },
};

/** Client-only pages. They exist for a visitor in the middle of something, so
 *  they answer 200 and are never indexed. */
const SHELL_PAGES: Array<{ match: RegExp; title: string }> = [
  { match: /^\/cart$/, title: 'Your cart' },
  { match: /^\/checkout\/success$/, title: 'Order confirmed' },
  { match: /^\/submit\/[^/]+$/, title: 'Submit your location' },
];

export type PageKind =
  /** A full page, rendered on the server. */
  | 'page'
  /** The app's empty document; the browser renders it. */
  | 'shell'
  /** No such page: a real 404. */
  | 'not-found'
  /** A page that needs the catalog, and the catalog is not available. */
  | 'unavailable';

export interface PageSeo {
  kind: PageKind;
  status: 200 | 404 | 503;
  title: string;
  description: string;
  /** Absolute, no parameters. Null on a page that is never indexed. */
  canonical: string | null;
  indexable: boolean;
  /** Absolute. */
  image: string | null;
  jsonLd: Array<Record<string, unknown>>;
}

const NOT_FOUND: Omit<PageSeo, 'kind' | 'status'> = {
  title: `Page not found | ${STORE}`,
  description: 'We could not find that page. Browse commercial and home EV chargers in the EV Initiative Store.',
  canonical: null,
  indexable: false,
  image: null,
  jsonLd: [],
};

/** A description short enough to show whole, cut at a word. */
export function metaDescription(text: string): string {
  const line = plainDescription(text);
  if (line.length <= DESCRIPTION_MAX) return line;
  const cut = line.slice(0, DESCRIPTION_MAX - 3);
  return `${cut.slice(0, Math.max(cut.lastIndexOf(' '), 0)).replace(/[\s,.;:]+$/, '')}...`;
}

const absolute = (path: string): string => `${SHOP_ORIGIN}${path}`;

/** The segment of a /product/<segment> path: a slug, an old slug or a database id. Null on any other path. */
export const productParam = (path: string): string | null => path.match(/^\/product\/([^/]+)$/)?.[1] ?? null;

/** Status, words and structured data for one path (no query string). */
export function seoForPath(path: string, catalog: StorePublicProduct[] | null): PageSeo {
  const page = STATIC_PAGES[path];
  if (page) {
    if (page.needsCatalog && !catalog) return { ...NOT_FOUND, kind: 'unavailable', status: 503 };
    return {
      kind: 'page',
      status: 200,
      title: page.title,
      description: page.description,
      canonical: absolute(path),
      indexable: true,
      image: absolute(page.image),
      jsonLd: [],
    };
  }

  const shell = SHELL_PAGES.find((s) => s.match.test(path));
  if (shell) {
    return {
      kind: 'shell',
      status: 200,
      title: `${shell.title} | ${STORE}`,
      description: NOT_FOUND.description,
      canonical: null,
      indexable: false,
      image: null,
      jsonLd: [],
    };
  }

  const why = path.match(/^\/why\/([^/]+)$/);
  const icp = why ? shopIcpBySlug(why[1]!) : null;
  if (icp) {
    return {
      kind: 'page',
      status: 200,
      title: `${icp.title} | ${STORE}`,
      description: metaDescription(icp.body),
      canonical: absolute(path),
      indexable: true,
      image: absolute(icp.poster),
      jsonLd: [],
    };
  }

  const param = productParam(path);
  if (param) {
    if (!catalog) return { ...NOT_FOUND, kind: 'unavailable', status: 503 };
    const found = resolveProduct(param, catalog);
    if (found) {
      const { product } = found;
      const family = familyForProduct(product, catalog);
      return {
        kind: 'page',
        status: 200,
        title: `${product.name} | ${STORE}`,
        description: metaDescription(productDescription(product, family)),
        // Always the product's own URL: the server moves an old URL before rendering, and the browser's head is right while
        // the client does the same.
        canonical: absolute(productPath(product)),
        indexable: true,
        image: family.media?.hero ? absolute(family.media.hero) : null,
        jsonLd: [productJsonLd(product, family, SHOP_ORIGIN)],
      };
    }
  }

  return { ...NOT_FOUND, kind: 'not-found', status: 404 };
}

/** The category paths, for the sitemap. */
export const CATEGORY_PATHS = SHOP_CATEGORIES.map((c) => `/${c.slug}`);
